import {
    Message,
    MessagePatientRequestBase,
} from "@accurx/api/patient-messaging";
import { NhsNumberHelpers } from "@accurx/shared";
import { MessageToSendData } from "domains/message-component/components/Compose/types/Compose.types";
import { SendMessageMutationVariables } from "domains/message-component/hooks/mutations/useSendMessageMutation";
import {
    Attachment,
    ContactDetail,
} from "domains/message-component/reducer.types";
import { v4 as uuid } from "uuid";

import { mapAssigneeToAsigneeDto } from "./mapAssigneeToAssigneeDto";
import { mapSelfBookToSelfBookDto } from "./mapSelfBookToSelfBookDto";
import { mapTemplateToMessageTemplateDto } from "./mapTemplateToMessageTemplateDto";

const mapPatientIdentifiers = (
    patientExternalIds: SendMessageMutationVariables["options"]["patientExternalIds"],
) => {
    return {
        nhsNumber:
            patientExternalIds.find(
                (id) =>
                    id.type === "NhsNumber" &&
                    NhsNumberHelpers.validateNhsNumber(id.value).success ===
                        true,
            )?.value ?? null,
    };
};

const mapContactDetailToMobileOrEmail = (contactDetails: ContactDetail) => {
    return {
        mobileNumber:
            contactDetails.method === "Mobile" ? contactDetails.value : null,
        emailAddress:
            contactDetails.method === "Email" ? contactDetails.value : null,
    };
};

const mapMessageTemplates = (
    composedMessage: SendMessageMutationVariables["composedMessage"],
) => {
    return {
        messageTemplate: mapTemplateToMessageTemplateDto({
            template: composedMessage.template,
            attachments: composedMessage.messages
                .map((msg) => msg.attachments)
                .flat(),
        }),
        conditionId:
            composedMessage.template.type === "QuestionnaireTemplate"
                ? composedMessage.template.value.id
                : null,
        conditionName:
            composedMessage.template.type === "QuestionnaireTemplate"
                ? composedMessage.template.value.title
                : null,
    };
};

const mapAttachmentsToAttachmentIds = (attachments: Attachment[]) => {
    return attachments
        .filter((att) => att.status === "success" && att.origin === "Upload")
        .map((att) => att.id);
};

const mapMessage = (message: MessageToSendData): Message => {
    return {
        messageBody: message.messageBody,
        attachedDocumentIds: mapAttachmentsToAttachmentIds(message.attachments),
        messageSignature: message.messageSignature,
        enablePatientResponse: message.isPatientResponseEnabled,
        nhsChoicesLink: message.nhsAdviceLink,
        sendTime:
            message.sendAt && message.sendAt.sendAtDateTime !== ""
                ? message.sendAt.sendAtDateTime
                : null,
        /**
         * Unique id created so if the request reaches the server,
         * but the response doesn't reach the client,
         * or there are server errors after having already sent the message,
         * and the client automatically retries it doesn't send
         * the same message twice
         * */
        clientMessageId: uuid(),
    };
};

const mapSingleSendMessage = ({
    composedMessage,
    options,
}: SendMessageMutationVariables): Required<MessagePatientRequestBase> => {
    const message = mapMessage(composedMessage.messages[0]);
    return {
        // Patient details
        ...mapPatientIdentifiers(options.patientExternalIds),

        // Patient contacts
        ...mapContactDetailToMobileOrEmail(composedMessage.contactDetails),

        // Clinician info
        organisationId: options.workspaceId,
        assignee: mapAssigneeToAsigneeDto(composedMessage.assignee),

        // Send info
        ticketIdentity: options.ticketIdentity,
        trySendViaNhsApp: options.trySendViaNhsApp,

        // MessageContent
        selfBook: mapSelfBookToSelfBookDto(composedMessage.selfBookLink),
        sendTime: message.sendTime ?? null,
        nhsChoicesLink: message.nhsChoicesLink ?? null,
        messageBody: message.messageBody,
        clientMessageId: message.clientMessageId,
        attachedDocumentIds: message.attachedDocumentIds,
        enablePatientResponse: message.enablePatientResponse,
        messageFooter: message.messageSignature,
        ...mapMessageTemplates(composedMessage),

        // Ignored
        isVideoConsult: false,
        videoConsultTime: null,
        patientListId: null,
        patientListEntryId: null,
        messages: null,
    };
};

export const mapSendMessageVariablesToBaseSendMessageRequest = ({
    composedMessage,
    options,
}: SendMessageMutationVariables): Required<MessagePatientRequestBase> => {
    if (composedMessage.messages.length === 1) {
        return mapSingleSendMessage({
            composedMessage,
            options,
        });
    }

    return {
        // Patient details
        ...mapPatientIdentifiers(options.patientExternalIds),

        // Patient contacts
        ...mapContactDetailToMobileOrEmail(composedMessage.contactDetails),

        // Clinician info
        organisationId: options.workspaceId,
        assignee: mapAssigneeToAsigneeDto(composedMessage.assignee),

        // Send info
        ticketIdentity: options.ticketIdentity,
        trySendViaNhsApp: options.trySendViaNhsApp,

        // Message content
        messages: composedMessage.messages.map(mapMessage),
        ...mapMessageTemplates(composedMessage),
        selfBook: null,

        // Ignored
        isVideoConsult: false,
        videoConsultTime: null,
        patientListId: null,
        patientListEntryId: null,

        // No sending anymore as it is included in messages array
        attachedDocumentIds: null,
        messageBody: null,
        messageFooter: null,
        enablePatientResponse: null,
        nhsChoicesLink: null,
        clientMessageId: null,
        sendTime: null,
    };
};
